import { h, type Component, type VNodeChild } from 'vue';
import { get } from 'lodash';
import { registerFormatter, ItpIcon } from '@integro/ui';
import type { FormatterFactory } from '@integro/ui';
import type { AttachmentsHelper, IAttachmentData } from './helpers/attachments';

export function registerGridFormatters(attachments: AttachmentsHelper) {
  registerFormatter('attachment', attachment(attachments));
  registerFormatter('statusDot', statusDot);
  registerFormatter('registerDocument', registerDocument);
  registerFormatter('dunningLetterBadges', dunningLetterBadges);
  registerFormatter('overdueHighlight', overdueHighlight);
}

const registerDocument: FormatterFactory = () => {
  function invoiceNumber(value: any): string | null {
    return value && typeof value === 'object' && value.invoice_number
      ? String(value.invoice_number)
      : null;
  }

  const renderer = (props: { value: any; context: any }): VNodeChild => {
    const invoice = invoiceNumber(props.value);
    if (invoice) {
      return h(
        'a',
        { href: '#', class: 'itp-data-grid__command command-link' },
        invoice,
      );
    }
    const reference = props.context?.reference_number;
    return reference != null ? h('span', null, String(reference)) : '';
  };

  renderer.props = ['value', 'context'];

  return {
    component: renderer as Component,
    props: (_value, context) => ({ context }),
    command: true,
    cellClass: (value, row) => {
      const isInvoice = invoiceNumber(value) != null;
      return {
        'fw-bold': true,
        'bg-yellow-500': isInvoice && !!row?.is_corrected,
        'text-yellow-800': isInvoice && !!row?.is_corrected,
        'bg-red-300': isInvoice && !!row?.is_reversed,
        'text-red-800': isInvoice && !!row?.is_reversed,
      };
    },
    toText: (value, row) =>
      invoiceNumber(value) ?? (row?.reference_number ?? '').toString(),
  };
};

interface DunningLetter {
  id?: number | string;
  status?: string;
  reference_number?: string;
  created_at?: string;
}

const DUNNING_STATUS_VARIANT: Record<string, string> = {
  sent: 'warning',
  delivered: 'success',
  error: 'danger',
};

function dunningVariant(letter: DunningLetter | null | undefined): string {
  return (
    (letter?.status && DUNNING_STATUS_VARIANT[letter.status]) || 'secondary'
  );
}

function dunningLabel(letter: DunningLetter | null | undefined): string {
  if (!letter?.created_at) {
    return '';
  }
  const date = new Date(letter.created_at);
  return Number.isNaN(date.getTime())
    ? ''
    : `${date.getMonth() + 1}.${date.getDate()}`;
}

const dunningLetterBadges: FormatterFactory = () => {
  const renderer = (props: { value: DunningLetter }): VNodeChild => {
    const letter = props.value;
    if (letter == null) {
      return null;
    }
    return h(
      'a',
      {
        href: '#',
        class: `itp-data-grid__command badge rounded-pill me-1 text-bg-${dunningVariant(letter)}`,
        title: letter.reference_number,
        'data-value': letter.id,
      },
      dunningLabel(letter),
    );
  };
  renderer.props = ['value'];

  const asLetters = (value: any): DunningLetter[] =>
    Array.isArray(value) ? value : value == null ? [] : [value];

  return {
    component: renderer as Component,
    command: true,
    toText: (value) =>
      Array.isArray(value)
        ? value.map(dunningLabel).join(' ')
        : dunningLabel(value),
    toValue: (value) =>
      asLetters(value)
        .map((letter) => {
          const label = dunningLabel(letter);
          return letter.reference_number
            ? `${label} [${letter.reference_number}]`
            : label;
        })
        .join(', '),
  };
};

function overdueLevel(value: any): number | null {
  if (typeof value === 'number' && value > 0) {
    return value < 35 ? 1 : value < 50 ? 2 : value < 65 ? 3 : 4;
  }
  return null;
}

const overdueHighlight: FormatterFactory = () => ({
  cellClass: (value) => {
    const level = overdueLevel(value);
    return level ? `overdue highlight-${level}` : '';
  },
  toText: (value) => (value == null ? '' : String(value)),
});

interface AttachmentOptions {
  displayFileName?: boolean;
  useDataContext?: boolean;
}

function attachment(attachments: AttachmentsHelper): FormatterFactory {
  return (options: AttachmentOptions = {}) => {
    function attachmentOf(value: any, context: any): IAttachmentData | null {
      const data = options.useDataContext ? context : value;
      return data != null && typeof data === 'object'
        ? (data as IAttachmentData)
        : null;
    }

    const renderer = (props: { value: any; context: any }): VNodeChild => {
      const data = attachmentOf(props.value, props.context);
      if (!data) {
        return null;
      }

      return h(
        'a',
        {
          href: '#',
          class: 'itp-data-grid__command command-link',
          title: data.file_name,
          'data-value': data.id,
        },
        [
          h(ItpIcon, { icon: attachments.getIconClass(data) }),
          options.displayFileName
            ? h('span', { class: 'ps-1' }, data.file_name)
            : null,
        ],
      );
    };

    renderer.props = ['value', 'context'];

    return {
      component: renderer as Component,
      props: (_value, context) => ({ context }),
      command: true,
      toText: (value, context) =>
        String(attachmentOf(value, context)?.file_name ?? ''),
    };
  };
}

interface StatusDotOptions {
  field?: string;
}

const statusDot: FormatterFactory = (options: StatusDotOptions = {}) => {
  const renderer = (
    props: { value: any; context: any },
    { slots }: { slots: Record<string, any> },
  ): VNodeChild => {
    const colors = get(globalThis, '$app.config.statuses.colors') as
      | Record<string, string>
      | undefined;
    const status = options.field
      ? get(props.context, options.field)
      : undefined;
    const color = status ? get(colors, status) : null;

    return [
      color ? h('span', { class: `me-2 ms-1 text-${color}` }, '⬤') : null,
      slots.default?.() ?? String(props.value ?? ''),
    ];
  };

  renderer.props = ['value', 'context'];

  return {
    component: renderer as Component,
    props: (_value, context) => ({ context }),
  };
};
